import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import { useData } from '../context/DataContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, 
  Plus, 
  Trash2, 
  Edit3, 
  X, 
  HelpCircle,
  Sparkles
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, setDoc, doc, deleteDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const AdminStrengths = () => {
  const { data } = useData();
  const strengths = data.strengths || [];
  const [editingItem, setEditingItem] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (editingItem) {
      setTitle(editingItem.title || '');
      setUploadedImageUrl(editingItem.image || '');
      document.body.classList.add('admin-modal-open');
    } else {
      setTitle('');
      setUploadedImageUrl('');
      document.body.classList.remove('admin-modal-open');
    }
    return () => document.body.classList.remove('admin-modal-open');
  }, [editingItem]);

  // Open modal for creating a new card
  const handleAddNew = () => {
    setEditingItem({ id: null, title: '', image: '' });
  };

  // Save Strength Item to Firestore
  const handleSaveItem = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      Swal.fire('Required Field', 'Please enter a title for the strength card.', 'warning');
      return;
    }

    const strengthData = {
      title: title.trim(),
      image: uploadedImageUrl,
      updatedAt: new Date().toISOString()
    };

    setIsSaving(true);
    Swal.fire({
      title: 'Saving Strength Card...',
      text: 'Updating Firestore database',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      if (editingItem?.id) {
        await setDoc(doc(db, 'strengths', editingItem.id), strengthData);
      } else {
        await addDoc(collection(db, 'strengths'), {
          ...strengthData,
          createdAt: new Date().toISOString()
        });
      }

      setIsSaving(false);
      await Swal.fire({
        title: 'Success',
        text: 'Strength card saved successfully',
        icon: 'success',
        confirmButtonColor: '#2d6a4f',
      });
      setEditingItem(null);
    } catch (error) {
      setIsSaving(false);
      console.error("Error saving strength:", error);
      Swal.fire('Error', 'Failed to save strength card', 'error');
    }
  };

  // Delete Strength Item from Firestore
  const handleDeleteItem = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This strength card will be permanently deleted!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete card'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'strengths', id));
        Swal.fire({
          title: 'Deleted!',
          text: 'Strength card removed.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error("Error deleting strength item:", error);
        Swal.fire('Error', 'Failed to delete strength card', 'error');
      }
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 font-poppins">
      
      {/* ── Top Header Bar ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div>
          <div className="flex items-center space-x-3 mb-1">
            <div className="p-2.5 bg-emerald/10 text-emerald rounded-2xl">
              <Zap size={24} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-charcoal">Manage Strengths</h2>
          </div>
          <p className="text-xs text-gray-500">
            Create, edit, and delete strength cards displayed on the public website.
          </p>
        </div>

        <button
          onClick={handleAddNew}
          className="flex items-center justify-center space-x-2 px-6 py-3.5 bg-sugarcane-green hover:bg-emerald text-white font-medium rounded-2xl shadow-lg shadow-sugarcane-green/20 transition-all duration-300 hover:scale-[1.02] active:scale-95"
        >
          <Plus size={18} />
          <span>New Strength Card</span>
        </button>
      </div>

      {/* ── Strengths Cards Roster ── */}
      {strengths.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
          <Sparkles className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-bold text-gray-700">No Strength Cards Present</h3>
          <p className="text-sm text-gray-400 mt-1 mb-6 max-w-sm mx-auto">
            There are currently no strength cards registered. Click "New Strength Card" to create one.
          </p>
          <button
            onClick={handleAddNew}
            className="inline-flex items-center space-x-2 px-5 py-2.5 bg-sugarcane-green text-white font-medium rounded-xl hover:bg-emerald transition-colors"
          >
            <Plus size={16} />
            <span>Create First Card</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {strengths.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group"
            >
              {/* Image Thumbnail */}
              <div className="relative h-48 bg-gray-50 overflow-hidden">
                {item.image ? (
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Sparkles size={32} />
                  </div>
                )}
                
                {/* Action Buttons Overlay */}
                <div className="absolute top-3 right-3 flex items-center space-x-2">
                  <button
                    onClick={() => setEditingItem(item)}
                    className="p-2 bg-white/90 backdrop-blur-md text-charcoal hover:text-sugarcane-green rounded-xl shadow-md hover:scale-110 transition-all"
                    title="Edit Card"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="p-2 bg-white/90 backdrop-blur-md text-red-500 hover:text-red-700 rounded-xl shadow-md hover:scale-110 transition-all"
                    title="Delete Card"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Card Details */}
              <div className="p-5 flex-grow flex flex-col justify-between">
                <h3 className="font-bold text-lg text-charcoal group-hover:text-sugarcane-green transition-colors">
                  {item.title}
                </h3>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* ── Modal Dialog for Create/Edit ── */}
      <AnimatePresence>
        {editingItem && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !isSaving && setEditingItem(null)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal Box */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden z-10 my-8"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-sugarcane-green/10 text-sugarcane-green rounded-xl">
                    <Zap size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">
                      {editingItem.id ? 'Edit Strength Card' : 'Create Strength Card'}
                    </h3>
                    <p className="text-xs text-gray-500">Provide card title and image (max 800 KB)</p>
                  </div>
                </div>
                
                <button 
                  type="button"
                  onClick={() => !isSaving && setEditingItem(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSaveItem} className="p-6 space-y-6">
                
                {/* Title Input */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                    Card Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Modern Machinery & High Extraction"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:bg-white focus:border-sugarcane-green focus:ring-4 focus:ring-sugarcane-green/10 transition-all text-sm outline-none"
                  />
                </div>

                {/* Image Upload Field (max 800 KB) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                      Card Image (Max 800 KB)
                    </label>
                    <span className="text-[10px] text-gray-400 flex items-center gap-1">
                      <HelpCircle size={12} /> Max 800 KB
                    </span>
                  </div>
                  <ImageUpload 
                    initialImage={uploadedImageUrl}
                    maxSizeKb={800}
                    folder="strengths"
                    onUploadSuccess={(url) => setUploadedImageUrl(url)}
                    disabled={isSaving}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => setEditingItem(null)}
                    className="px-5 py-2.5 text-gray-600 hover:text-gray-900 font-medium text-sm rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2.5 bg-sugarcane-green hover:bg-emerald text-white font-medium text-sm rounded-xl shadow-lg shadow-sugarcane-green/20 transition-all duration-200 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : (editingItem.id ? 'Update Card' : 'Create Card')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminStrengths;
